import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, MessageCircle } from 'lucide-react';

/**
 * QuizQuestionContext — Floating WhatsApp-style bubble with Sabril's Pixar avatar.
 *
 * Two pieces:
 *  1. Floating bubble (bottom-right) that opens the explanation in a chat-like panel
 *  2. Inside the panel: why + benefit + confidentiality footer
 *
 * Always pinned at bottom-right of the screen. Restaurateur can dismiss with X
 * or by clicking outside. Auto-suggests opening on each new question.
 */

const SABRIL_PIXAR_AVATAR = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/6ozjn0ci_Story%20Espace%20Professionnel%20-%20%20Instagram%20%28Your%20Story%29.png.jpg';

const QuizQuestionContext = ({ why, benefit, confidential = false, questionId }) => {
  // Bubble closed by default — user opens it if they want to know why
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  // Reset state on question change (still closed by default)
  useEffect(() => {
    setOpen(false);
    setDismissed(true);
  }, [questionId]);

  if (!why && !benefit) return null;

  return (
    <>
      {/* === Floating launcher button (always visible) === */}
      <motion.button
        initial={{ scale: 0, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 280, damping: 18 }}
        onClick={() => { setOpen(!open); setDismissed(open); }}
        className="fixed bottom-24 right-4 z-[55] flex items-center gap-2 rounded-full p-1 pr-3 transition-shadow"
        data-testid="quiz-context-bubble-trigger"
        style={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #2A1810 100%)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.35), 0 0 22px rgba(240,90,26,0.32)',
          border: '1px solid rgba(240,90,26,0.45)',
        }}
        aria-label="Pourquoi Sabril te demande ça"
      >
        {/* Pixar avatar with breathing pulse */}
        <motion.div
          className="relative w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #FBBF24 0%, #F05A1A 100%)',
            boxShadow: '0 0 0 2px rgba(255,255,255,0.18) inset',
          }}
          animate={{ scale: [1, 1.04, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img
            src={SABRIL_PIXAR_AVATAR}
            alt="Sabril"
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 18%' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {/* Online dot */}
          <span
            className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
            style={{ background: '#22C55E', borderColor: '#1A1A1A' }}
          />
        </motion.div>
        <div className="text-left leading-tight">
          <p className="text-[10px] font-black uppercase tracking-wider text-[#F05A1A]">Sabril</p>
          <p className="text-[10px] font-bold text-white/85">
            {open ? 'Ferme la bulle' : 'Pourquoi ?'}
          </p>
        </div>
        {/* Notification dot when closed (after dismiss) */}
        {!open && (
          <motion.span
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
            style={{ background: '#EF4444', boxShadow: '0 0 8px rgba(239,68,68,0.7)' }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
        )}
      </motion.button>

      {/* === Floating panel (WhatsApp-style chat bubble) === */}
      <AnimatePresence>
        {open && !dismissed && (
          <>
            {/* Backdrop — invisible, only catches the click-to-close */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 z-[54]"
              onClick={() => setDismissed(true)}
              data-testid="quiz-context-backdrop"
            />

            <motion.div
              key="panel"
              initial={{ opacity: 0, y: 24, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              className="fixed bottom-[6.5rem] right-4 z-[56] w-[calc(100vw-2rem)] sm:w-[360px] max-w-[360px] rounded-3xl overflow-hidden"
              data-testid="quiz-context-bubble-panel"
              style={{
                background: 'linear-gradient(180deg, #1A1A1A 0%, #2A1810 100%)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.55), 0 0 36px rgba(240,90,26,0.32)',
                border: '1px solid rgba(240,90,26,0.35)',
              }}
            >
              {/* Header with close */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-white/8">
                <motion.div
                  className="relative w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #FBBF24 0%, #F05A1A 100%)',
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.18) inset, 0 0 18px rgba(240,90,26,0.5)',
                  }}
                  animate={{ y: [0, -1.5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <img
                    src={SABRIL_PIXAR_AVATAR}
                    alt="Sabril"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 18%' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span
                    className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
                    style={{ background: '#22C55E', borderColor: '#1A1A1A' }}
                  />
                </motion.div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-black text-sm leading-tight">Sabril</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <p className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider">En ligne</p>
                  </div>
                </div>
                <button
                  onClick={() => setDismissed(true)}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                  data-testid="quiz-context-close-btn"
                  aria-label="Fermer"
                >
                  <X className="w-4 h-4 text-white/80" strokeWidth={2.5} />
                </button>
              </div>

              {/* Chat content */}
              <div className="px-4 py-4 space-y-3 max-h-[55vh] overflow-y-auto">
                {/* Why bubble (incoming) */}
                {why && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 }}
                    className="rounded-2xl rounded-tl-md px-3.5 py-2.5"
                    style={{ background: 'rgba(240,90,26,0.10)', border: '1px solid rgba(240,90,26,0.20)' }}
                  >
                    <p className="text-[9px] font-black tracking-[2px] uppercase mb-1.5" style={{ color: '#F05A1A' }}>
                      Pourquoi je te demande ça
                    </p>
                    <p className="text-white text-[13px] leading-snug font-medium">
                      {why}
                    </p>
                  </motion.div>
                )}

                {/* Benefit bubble (incoming) */}
                {benefit && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35 }}
                    className="rounded-2xl rounded-tl-md px-3.5 py-2.5"
                    style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)' }}
                  >
                    <p className="text-[9px] font-black tracking-[2px] uppercase mb-1.5" style={{ color: '#4ADE80' }}>
                      🎯 Ce que tu y gagnes
                    </p>
                    <p className="text-white/90 text-[13px] leading-snug">
                      {benefit}
                    </p>
                  </motion.div>
                )}

                {/* Confidentiality footer */}
                {confidential && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.55 }}
                    className="flex items-center gap-2 rounded-xl px-3 py-2"
                    style={{ background: 'rgba(255,255,255,0.04)' }}
                  >
                    <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#4ADE80' }} strokeWidth={2.5} />
                    <p className="text-white/65 text-[10px] leading-tight">
                      <strong className="text-white">100% confidentiel.</strong> Tes chiffres servent uniquement à calculer ton audit · jamais partagés.
                    </p>
                  </motion.div>
                )}
              </div>

              {/* Typing indicator footer */}
              <div className="px-4 py-2 border-t border-white/8 flex items-center gap-2">
                <MessageCircle className="w-3 h-3" style={{ color: '#F05A1A' }} />
                <p className="text-[10px] text-white/55 italic">Sabril t'accompagne à chaque étape</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuizQuestionContext;
